'use client';

import { useState, useEffect } from 'react';
import NewsFilters from './NewsFilters';
import NewsArticleCard from './NewsArticleCard';
import NewsPagination from './NewsPagination';
import AdZone from './AdZone';
import LoadingSkeleton from './LoadingSkeleton';
import { articleService } from '@/services/articleService';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  featuredImage: {
    url: string;
    alt: string;
  };
  author: string;
  publishedAt: string;
  category: string;
  categoryColor?: string;
  readTime: number;
  tags: string[];
  isFeatured?: boolean;
  slug: string;
}

interface FilterState {
  category: string;
  dateRange: string;
  searchTerm: string;
  sortBy: 'date' | 'popularity' | 'relevance';
}

const NewsHubInteractive = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    category: '',
    dateRange: '',
    searchTerm: '',
    sortBy: 'date'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resultCount, setResultCount] = useState(0);
  const articlesPerPage = 12;

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data, error: fetchError } = await articleService.getAllPublishedArticles();

        if (fetchError) {
          throw fetchError;
        }

        if (!data || data.length === 0) {
          setArticles([]);
          setIsLoading(false);
          return;
        }

        // Transform Supabase data to Article interface
        const transformedArticles: Article[] = data.map((article: any) => ({
          id: article.id,
          title: article.title,
          excerpt: article.excerpt || '',
          featuredImage: {
            url: article.featured_image_url || '',
            alt: article.featured_image_alt || article.title
          },
          author: article.user_profiles?.full_name || 'Anonymous',
          publishedAt: article.published_date || article.created_at,
          category: article.article_categories?.name || 'Uncategorized',
          categoryColor: article.article_categories?.color || '#1a1a1a',
          readTime: article.read_time_minutes || 5,
          tags: article.keywords || [],
          isFeatured: article.is_featured || false,
          slug: article.slug
        }));

        setArticles(transformedArticles);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError(err instanceof Error ? err.message : 'Failed to load articles');
        setIsLoading(false);
      }
    };

    fetchArticles();
  }, []);

  useEffect(() => {
    let filtered = [...articles];

    // Apply category filter
    if (filters.category) {
      filtered = filtered.filter((article) => article.category === filters.category);
    }

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter((article) =>
        article.title.toLowerCase().includes(searchLower) ||
        article.excerpt.toLowerCase().includes(searchLower) ||
        article.tags.some((tag) => tag.toLowerCase().includes(searchLower))
      );
    }

    // Apply date range filter
    if (filters.dateRange) {
      const now = new Date();
      const days = parseInt(filters.dateRange);
      const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      filtered = filtered.filter((article) => new Date(article.publishedAt) >= cutoffDate);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'date':
          return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        case 'popularity':
          return (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0);
        case 'relevance':
          if (filters.searchTerm) {
            const aRelevance = a.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) ? 1 : 0;
            const bRelevance = b.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) ? 1 : 0;
            return bRelevance - aRelevance;
          }
          return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        default:
          return 0;
      }
    });

    setFilteredArticles(filtered);
    setResultCount(filtered.length);
    setCurrentPage(1);
  }, [articles, filters]);

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      dateRange: '',
      searchTerm: '',
      sortBy: 'date'
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get current articles for pagination
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);

  // Get featured articles
  const featuredArticles = filteredArticles.filter((article) => article.isFeatured).slice(0, 2);
  const regularArticles = currentArticles.filter((article) => !article.isFeatured || !featuredArticles.includes(article));

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <h3 className="text-lg font-heading font-bold text-card-foreground mb-2">
            Error Loading Articles
          </h3>
          <p className="text-text-secondary mb-4">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-sm hover:bg-accent transition-industrial"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground uppercase tracking-wide mb-4">
            Construction News Hub
          </h1>
          <p className="text-lg text-text-secondary max-w-3xl">
            Comprehensive coverage of construction industry developments.
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <h3 className="text-lg font-heading font-bold text-card-foreground mb-2">
            No Articles Published Yet
          </h3>
          <p className="text-text-secondary">
            Check back soon for the latest construction industry news and insights.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground uppercase tracking-wide mb-4">
          Construction News Hub
        </h1>
        <p className="text-lg text-text-secondary max-w-3xl">
          Comprehensive coverage of construction industry developments, featuring both international insights and Irish market focus with advanced filtering capabilities.
        </p>
      </div>

      {/* Filters Section */}
      <div className="mb-8">
        <NewsFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          resultCount={resultCount}
          articles={articles} />

      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-text-secondary">
          Showing {indexOfFirstArticle + 1}-{Math.min(indexOfLastArticle, filteredArticles.length)} of {resultCount} articles
        </div>
        <div className="text-sm text-text-secondary">
          Last updated: {new Date().toLocaleDateString()}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Featured Articles */}
          {featuredArticles.length > 0 && currentPage === 1 &&
          <div className="mb-8">
              <h2 className="text-xl font-heading font-bold text-foreground uppercase tracking-wide mb-6">
                Featured Stories
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {featuredArticles.map((article) =>
              <NewsArticleCard
                key={`featured-${article.id}`}
                article={article}
                variant="featured" />

              )}
              </div>
            </div>
          }

          {/* Regular Articles */}
          {regularArticles.length > 0 ?
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {regularArticles.map((article) =>
            <NewsArticleCard
              key={article.id}
              article={article}
              variant="standard" />

            )}
            </div> :

          <div className="bg-card border border-border rounded-lg p-8 text-center">
              <h3 className="text-lg font-heading font-bold text-card-foreground mb-2">
                No Articles Found
              </h3>
              <p className="text-text-secondary mb-4">
                Try adjusting your filters or search terms to find more articles.
              </p>
              <button
              onClick={clearFilters}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-sm hover:bg-accent transition-industrial">

                Clear All Filters
              </button>
            </div>
          }

          {/* Pagination */}
          {totalPages > 1 &&
          <div className="mt-8">
              <NewsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange} />

            </div>
          }
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            {/* Ad Zone */}
            <AdZone />
            
            {/* Newsletter Signup */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground mb-3">
                Stay Updated
              </h3>
              <p className="text-text-secondary text-sm mb-4">
                Get the latest construction industry news delivered to your inbox.
              </p>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 border border-border rounded-industrial bg-background text-foreground text-sm focus:border-primary focus:outline-none" />

                <button className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-xs hover:bg-accent transition-industrial">
                  Subscribe
                </button>
              </div>
            </div>

            {/* Popular Topics */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground mb-4">
                Popular Topics
              </h3>
              <div className="space-y-2">
                {['Equipment Innovation', 'Safety & Regulations', 'Market Analysis', 'Infrastructure Projects'].map((topic) =>
                <button
                  key={topic}
                  onClick={() => handleFilterChange({ category: topic })}
                  className="block w-full text-left text-sm text-text-secondary hover:text-primary transition-industrial py-1">

                    {topic}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default NewsHubInteractive;